/**
 * `W5-T07` §4.3–§4.5, §7 — what every billing screen shares: which statuses are live, watched or past
 * due, the writes the billing route's action makes, and what a refusal means. Pure where it can be,
 * and keys, never sentences.
 *
 * **Leaving for Stripe is a redirect, not a `window` call** (`features/payouts/onboarding.ts`): React
 * Router turns a `redirect()` to another origin into `window.location.assign(url)`, so Checkout and
 * the portal are pages the provider can come back from with Back, and nothing here touches a browser
 * global (ADR-011 R2). A hosted page is asked for on the click, never cached, and never logged.
 *
 * Stripe.js is not loaded here, nor anywhere under `features/billing` (AC 38,
 * `MEM-2026-10-17-money-1`): both hosted pages are redirects.
 */
import { redirect, type LoaderFunctionArgs } from 'react-router';
import {
  SubscriptionCheckoutInputSchema,
  SubscriptionPlanInputSchema,
  type ProviderSubscription,
  type SubscriptionCheckoutInput,
  type SubscriptionPlanInput,
  type SubscriptionPlans,
} from '@marketplace/contracts';
import { ApiError } from '../../shared/api-error.js';
import { queryKeys, routeContext } from '../../shared/query.js';
import { becomeAProPath } from '../payouts/onboarding.js';

export type PlanKind = SubscriptionPlanInput['kind'];
export type SubscriptionState = ProviderSubscription['status'];
export type Plan = SubscriptionPlans['plans'][number];
/** Where Checkout was started from: its `success_url` and `cancel_url` say it back (§4.3). */
export type CheckoutFrom = SubscriptionCheckoutInput['from'];

/** The statuses §4.3 step 3 calls live: the provider has a subscription, so they manage it. */
const LIVE: readonly SubscriptionState[] = [
  'INCOMPLETE',
  'TRIALING',
  'ACTIVE',
  'PAST_DUE',
  'UNPAID',
  'PAUSED',
];

/** Back from Checkout, the statuses the webhook can still move (§4.10): no row yet, or 3DS pending. */
const WATCHED: readonly SubscriptionState[] = ['NONE', 'INCOMPLETE'];

/** A renewal failed: the banner, and the way to a new card (§4.10). */
const PAST_DUE: readonly SubscriptionState[] = ['PAST_DUE', 'UNPAID'];

export function isLive(status: SubscriptionState): boolean {
  return LIVE.includes(status);
}

export function isWatched(status: SubscriptionState): boolean {
  return WATCHED.includes(status);
}

export function isPastDue(status: SubscriptionState): boolean {
  return PAST_DUE.includes(status);
}

/** §4.4 step 2: only a paid-up subscription with no cancellation scheduled changes plan. */
export function canSwitch(subscription: ProviderSubscription): boolean {
  return (
    subscription.status === 'ACTIVE' &&
    !subscription.cancelAtPeriodEnd &&
    subscription.plan !== null
  );
}

export function otherKind(kind: PlanKind): PlanKind {
  return kind === 'PRO' ? 'MANITAS' : 'PRO';
}

/** The plan for a kind, or `null` when the plans could not be read. */
export function planFor(plans: SubscriptionPlans | null, kind: PlanKind): Plan | null {
  return plans?.plans.find((plan) => plan.kind === kind) ?? null;
}

export function billingPath(locale: string): string {
  return `/${locale}/account/billing`;
}

/** `W5-T13` §4.4 — the subscription step of becoming a provider: where `become-a-pro` lands. */
export function billingStepPath(locale: string): string {
  return `${billingPath(locale)}?step=registration`;
}

/** `W5-T13` §4.6 — the step after it, `W5-T01`'s, unchanged: Connect with Stripe. */
export function payoutsStepPath(locale: string): string {
  return `/${locale}/account/payouts?step=registration`;
}

/**
 * `W5-T13` §3 — why a provider who is not entitled is not: they never subscribed (no row, or only a
 * first payment that never completed), or they did and it lapsed. The two are told apart because the
 * lapsed provider has open quotes and booked jobs, and is told that those still stand.
 */
const LAPSED: readonly SubscriptionState[] = ['CANCELED', 'UNPAID', 'PAUSED'];

export type GateState = 'none' | 'lapsed';

/**
 * What the subscription banner says, or `null` for nothing: an entitled provider, and a subscription
 * that could not be read, where the quote and bid routes stay the judge (`PayoutsPendingBanner`'s
 * rule). The API answers `NONE` when a provider's only rows are `CANCELED` (`W5-T07`), so in practice
 * the lapsed copy is for `UNPAID` and `PAUSED` until it answers otherwise.
 */
export function gateStateOf(subscription: ProviderSubscription | null): GateState | null {
  if (subscription === null || subscription.entitled) return null;
  return LAPSED.includes(subscription.status) ? 'lapsed' : 'none';
}

/** The closed set, or `fallback`: the field is the page's own, but a form is text anybody can post. */
export function checkoutFromOf(
  value: FormDataEntryValue | null,
  fallback: CheckoutFrom = 'account',
): CheckoutFrom {
  const parsed = SubscriptionCheckoutInputSchema.shape.from.safeParse(value);
  return parsed.success ? parsed.data : fallback;
}

/**
 * The renewal or end date in the reader's language. The API sends an instant; a provider reads a day
 * (*18 de noviembre de 2026*), so no time and no zone.
 */
export function formatPeriodEnd(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(new Date(iso));
}

/**
 * The subscription, or `null` when it could not be read: for `/account`'s banner, where a client
 * (`403`), a provider role with no profile (`409`) and an API that failed all mean "no banner", never
 * a broken page (§4.10). `W5-T13` reads it the same way on the feed pages and `/account/pro`.
 */
export async function loadMySubscription(
  context: LoaderFunctionArgs['context'],
  { fresh = true }: { fresh?: boolean } = {},
): Promise<ProviderSubscription | null> {
  const { queryClient, api } = context.get(routeContext);
  return queryClient
    .fetchQuery({
      queryKey: queryKeys.mySubscription(),
      queryFn: () => api.getMySubscription(),
      // `W5-T13`: the feed pages poll every five seconds and take the cache, as `loadMyPayouts`
      // does; a refusal and the billing page's own reads refresh it.
      ...(fresh ? { staleTime: 0 } : {}),
    })
    .catch(() => null);
}

export type SubscriptionRefusal = 'billing.gate.refused';

/**
 * `W5-T13` §7: `SUBSCRIPTION_REQUIRED` on a quote or bid, from a page that was stale about it. The
 * refusal's sentence, or `null` for any other refusal, which the form's own mapping reads.
 */
export function subscriptionRefusalKey(error: unknown): SubscriptionRefusal | null {
  return error instanceof ApiError && error.message === 'SUBSCRIPTION_REQUIRED'
    ? 'billing.gate.refused'
    : null;
}

export function isSubscriptionRefusal(key: string | null): key is SubscriptionRefusal {
  return key === 'billing.gate.refused';
}

export type BillingIntent = 'subscribe' | 'portal' | 'switch';

export type BillingFailure =
  | 'billing.error.exists'
  | 'billing.error.not_found'
  | 'billing.error.not_changeable'
  | 'billing.error.plan_payment_failed'
  | 'billing.error.unavailable';

export type BillingActionData =
  { intent: BillingIntent; failure: BillingFailure } | { intent: 'switch'; switched: true };

/**
 * A refusal, as what the provider reads (§7). Anything without a sentence of its own,
 * `503 PAYMENTS_UNAVAILABLE` included, is "we could not reach Stripe".
 */
export function billingFailureKey(error: unknown): BillingFailure {
  if (!(error instanceof ApiError)) return 'billing.error.unavailable';
  switch (error.message) {
    case 'SUBSCRIPTION_EXISTS':
      return 'billing.error.exists';
    case 'SUBSCRIPTION_NOT_FOUND':
      return 'billing.error.not_found';
    case 'SUBSCRIPTION_NOT_CHANGEABLE':
      return 'billing.error.not_changeable';
    case 'SUBSCRIPTION_PAYMENT_FAILED':
      return 'billing.error.plan_payment_failed';
    default:
      return 'billing.error.unavailable';
  }
}

function intentOf(value: FormDataEntryValue | null): BillingIntent | null {
  return value === 'subscribe' || value === 'portal' || value === 'switch' ? value : null;
}

function kindOf(value: FormDataEntryValue | null): PlanKind | null {
  const parsed = SubscriptionPlanInputSchema.shape.kind.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * The server already checks the host (§4.3 step 8, §4.5). Checking again costs one line and means
 * this page can never be the one that sends a provider somewhere else.
 */
function isHostedPage(url: string, host: 'checkout.stripe.com' | 'billing.stripe.com'): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && parsed.hostname === host;
  } catch {
    return false;
  }
}

/**
 * The billing route's three writes, told apart by `intent`: Subscribe (Checkout), Manage billing and
 * Update card (the portal), and Switch. The first two answer the redirect to Stripe; Switch is posted
 * by a fetcher, so its answer reaches the dialog that asked, and a `200` re-reads the subscription and
 * the profile, whose kind moved with it (§4.4 step 6).
 *
 * A `403` or `CONFLICT` is somebody with no provider profile: `become-a-pro`, as the payouts page
 * does. Any other refusal re-reads the subscription, because it may have changed under the page (a
 * `SUBSCRIPTION_EXISTS` is a row the page did not know about).
 */
export async function runBillingAction(
  context: LoaderFunctionArgs['context'],
  locale: string,
  form: FormData,
): Promise<BillingActionData | Response> {
  const intent = intentOf(form.get('intent'));
  // Not user-reachable: the page's own forms send one of the three.
  if (intent === null) throw new Response(null, { status: 400 });

  const { queryClient, api } = context.get(routeContext);
  try {
    if (intent === 'subscribe') {
      // `W5-T13`: the button says where it is (the registration step, a quote, a bid), so Checkout
      // comes back there; one without a `from` is this page's own, the account's.
      const { url } = await api.startSubscriptionCheckout(checkoutFromOf(form.get('from')));
      return isHostedPage(url, 'checkout.stripe.com')
        ? redirect(url)
        : { intent, failure: 'billing.error.unavailable' };
    }
    if (intent === 'portal') {
      const { url } = await api.openBillingPortal();
      return isHostedPage(url, 'billing.stripe.com')
        ? redirect(url)
        : { intent, failure: 'billing.error.unavailable' };
    }

    const kind = kindOf(form.get('kind'));
    if (kind === null) throw new Response(null, { status: 400 });
    await api.changeSubscriptionPlan(kind);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.mySubscription(), refetchType: 'all' }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.myProviderProfile(),
        refetchType: 'all',
      }),
    ]);
    return { intent, switched: true };
  } catch (error) {
    if (error instanceof Response) throw error;
    if (error instanceof ApiError && error.status === 401) return redirect(`/${locale}/login`);
    if (error instanceof ApiError && (error.status === 403 || error.message === 'CONFLICT')) {
      return redirect(becomeAProPath(locale));
    }
    await queryClient.invalidateQueries({
      queryKey: queryKeys.mySubscription(),
      refetchType: 'all',
    });
    return { intent, failure: billingFailureKey(error) };
  }
}
